/**
 * Browser-side image compression for uploads.
 *
 * Menu/profile images are only ever shown small (cards, thumbnails), so we
 * downscale + recompress in the browser before sending. This is what keeps the
 * base64 menu payload (POST /menu/base64, PUT /menu-items/:id) under the 10 MB
 * express.json limit — i.e. it is the fix for the 413 Request Entity Too Large
 * that large phone photos used to trigger. No backend or nginx change needed.
 *
 * Pure canvas — no npm dependency. Output defaults to WebP (which the backend
 * already accepts) and falls back to JPEG where WebP encode is unsupported.
 *
 * Usage:
 *   const { dataUrl, file } = await compressImage(originalFile);
 *   // base64 path  -> send dataUrl as imageData
 *   // multipart path-> fd.append('image', file)
 */

// Types canvas can't faithfully re-encode (vector / animation) — pass through.
const PASSTHROUGH_TYPES = new Set(['image/svg+xml', 'image/gif']);

let _webpSupported = null;
/** Detect (once) whether canvas.toBlob/toDataURL can emit WebP in this browser. */
function supportsWebp() {
  if (_webpSupported !== null) return _webpSupported;
  try {
    const c = document.createElement('canvas');
    c.width = c.height = 1;
    _webpSupported = c.toDataURL('image/webp').startsWith('data:image/webp');
  } catch {
    _webpSupported = false;
  }
  return _webpSupported;
}

/** Read a File/Blob as a data URL. */
function readAsDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('read failed'));
    reader.readAsDataURL(blob);
  });
}

/**
 * Decode a file into something drawable, honoring EXIF orientation so portrait
 * phone photos aren't rotated. Prefers createImageBitmap (which applies
 * orientation), falling back to an <img> element.
 * @returns {Promise<{ source: CanvasImageSource, width: number, height: number, cleanup: Function }>}
 */
async function decodeImage(file) {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        cleanup: () => bitmap.close && bitmap.close(),
      };
    } catch {
      // Fall through to the <img> path (e.g. older Safari / option unsupported).
    }
  }

  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('decode failed'));
      el.src = url;
    });
    return {
      source: img,
      width: img.naturalWidth || img.width,
      height: img.naturalHeight || img.height,
      cleanup: () => URL.revokeObjectURL(url),
    };
  } catch (err) {
    URL.revokeObjectURL(url);
    throw err;
  }
}

/** Promise wrapper around canvas.toBlob. */
function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

/**
 * Downscale + recompress an image entirely in the browser.
 *
 * @param {File} file                 The user-selected file.
 * @param {object} [opts]
 * @param {number} [opts.maxDimension=1600]  Longest side, in px (never upscales).
 * @param {number} [opts.targetBytes=1000000] Aim for an output at or below this size.
 * @param {string} [opts.mimeType='image/webp'] Preferred output type; JPEG fallback.
 * @returns {Promise<{ dataUrl: string, blob: Blob, file: File }>}
 * @throws if the file is not an image or cannot be decoded.
 */
export async function compressImage(file, opts = {}) {
  const {
    maxDimension = 1600,
    targetBytes = 1_000_000,
    mimeType = 'image/webp',
  } = opts;

  if (!file || !file.type || !file.type.startsWith('image/')) {
    throw new Error('Not an image file');
  }

  // Vector / animated formats, or already-small files within bounds: pass through
  // unchanged so we don't rasterize SVG, drop GIF animation, or lose quality.
  if (PASSTHROUGH_TYPES.has(file.type)) {
    return { dataUrl: await readAsDataUrl(file), blob: file, file };
  }

  const { source, width, height, cleanup } = await decodeImage(file);
  try {
    const longest = Math.max(width, height);
    const scale = longest > maxDimension ? maxDimension / longest : 1;

    // Small enough already and no resize needed -> keep the original bytes.
    if (scale === 1 && file.size <= targetBytes) {
      return { dataUrl: await readAsDataUrl(file), blob: file, file };
    }

    const targetW = Math.max(1, Math.round(width * scale));
    const targetH = Math.max(1, Math.round(height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(source, 0, 0, targetW, targetH);

    const outType = mimeType === 'image/webp' && !supportsWebp() ? 'image/jpeg' : mimeType;

    // Step quality down until we're under the target (or hit the floor), keeping
    // the smallest result we produced.
    let best = null;
    for (let quality = 0.85; quality >= 0.5; quality -= 0.1) {
      // eslint-disable-next-line no-await-in-loop
      const blob = await canvasToBlob(canvas, outType, quality);
      if (!blob) break;
      if (!best || blob.size < best.size) best = blob;
      if (blob.size <= targetBytes) break;
    }

    if (!best) {
      // Encode failed entirely — fall back to the original file.
      return { dataUrl: await readAsDataUrl(file), blob: file, file };
    }

    // If compression somehow produced something larger than the original, keep
    // the original (e.g. an already-optimized small JPEG).
    if (best.size >= file.size && scale === 1) {
      return { dataUrl: await readAsDataUrl(file), blob: file, file };
    }

    const ext = best.type === 'image/webp' ? 'webp' : 'jpg';
    const baseName = (file.name || 'image').replace(/\.[^.]+$/, '');
    const outFile = new File([best], `${baseName}.${ext}`, { type: best.type });

    return { dataUrl: await readAsDataUrl(best), blob: best, file: outFile };
  } finally {
    cleanup && cleanup();
  }
}

export default compressImage;
